import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { BulkAssignCareManagerWrapper } from 'src/app/core/models/bulk-assign-care-manager-wrapper';
import { CareTeamMember } from 'src/app/core/models/care-team-member';
import { CaseLoad } from 'src/app/core/models/case-load';
import { JSONResponse, JSONResponseStatus } from 'src/app/core/models/json-response';
import { PatientCareManager } from 'src/app/core/models/patient-care-manager';
import { PatientCareSpecialist } from 'src/app/core/models/patient-care-specialist';
import { PatientCareTeamTrigger } from 'src/app/core/models/patient-care-team-triggers';
import { PatientExternalAgencyUsers } from 'src/app/core/models/patient-external-agency-user';
import { BulkAssignCareGiverWrapper } from '../models/bulk-assign-care-giver-wrapper';
import { BulkAssignExternalAgencyUserWrapper } from '../models/bulk-assign-external-agency-user-wrapper';
import { CareTeamProvider } from '../models/care-team-provider';
import { ICTMember } from '../models/ict-member';

const getCareTeamUrl = '/patients/getPatientCareTeam.html';
const getPatientAssignCareTeamUrl = '/patients/getPatientAssignCareTeam.html';
const assignCareManagerUrl = '/patients/assignCareManager.html';
const assignExternalAgencyUserUrl = '/patients/assignExternalAgencyUser.html';
const unassignCareManagerUrl = '/patients/UnassignCareManager.html';
const unassignExternalAgencyUserUrl = '/patients/UnassignExternalAgencyUser.html';
const getCareTeamTriggerUrl = '/patients/getAssignCareTeamTriggers.html';
const getCareTeamRolesUrl = '/patients/getCareTeamRoles.html';
const getCaseloadUrl = '/reports/getCareManagerCaseloadByPlanandRisk.html';
const getRiskBaseCaseLoadUrl = '/reports/getRiskBaseCaseLoad.html';
const assignCareGiverUrl = '/patients/assignPatientCareGiver.html';
const revokePatientCareGiverUrl = '/patients/revokePatientCareGiver.html';
const reactivatePatientCareGiverUrl = '/patients/reactivatePatientCareGiver.html';
const getPatientAssignCareTeamHistoryUrl = '/patients/getPatientAssignCareTeamHistory.html';
const assignICTMemberUrl = '/patients/assignICTMember.html';
const assignCareSpecialistUrl = '/patients/assignCareSpecialist.html';
const unassignICTMemberUrl = '/patients/UnassignICTMember.html';
const unassignCareSpecialistUrl = '/patients/UnassignCareSpecialist.html';
const updateICTMemberUrl = '/patients/UpdateICTMember.html';
const updateCareSpecialistUrl = '/patients/UpdateCareSpecialist.html';
const careTeamMemberRoleExistsUrl = '/patients/careTeamMemberRoleExists.html';
const careTeamExternalAgencyMemberRoleExistsUrl = '/patients/careTeamExternalAgencyMemberRoleExists.html';
const assignCareTeamProviderUrl = '/patients/assignCareTeamProvider.html';
const unassignCareTeamProviderUrl = '/patients/unassignCareTeamProvider.html';
const updateCareTeamProviderUrl = '/patients/updateCareTeamProvider.html';

@Injectable({
  providedIn: 'root'
})
export class CareTeamService {
  private _careTeamUpdated: Subject<boolean> = new Subject();
  careTeamUpdated: Observable<boolean> = this._careTeamUpdated.asObservable();

  constructor(private http: HttpClient) {}

  addCareManager(bulkAssignCaremanagerWrapper: BulkAssignCareManagerWrapper): Observable<boolean> {
    return this.http.post<boolean>(assignCareManagerUrl, bulkAssignCaremanagerWrapper).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  addExternalAgencyUser(bulkAssignExternalAgencyUserWrapper: BulkAssignExternalAgencyUserWrapper): Observable<boolean> {
    return this.http.post<boolean>(assignExternalAgencyUserUrl, bulkAssignExternalAgencyUserWrapper).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  addCareGiver(bulkAssignCaregiverWrapper: BulkAssignCareGiverWrapper): Observable<boolean> {
    return this.http.post<boolean>(assignCareGiverUrl, bulkAssignCaregiverWrapper).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  assignICTMember(ictMember: ICTMember): Observable<boolean> {
    return this.http.post<boolean>(assignICTMemberUrl, ictMember).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  assignCareSpecialist(patientCareSpecialist: PatientCareSpecialist): Observable<boolean> {
    return this.http.post<boolean>(assignCareSpecialistUrl, patientCareSpecialist).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  unassignCareManager(patientCareManager: PatientCareManager): Observable<boolean> {
    return this.http.post<boolean>(unassignCareManagerUrl, patientCareManager).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  unassignExternalAgencyUser(patientExternalAgencyUser: PatientExternalAgencyUsers): Observable<boolean> {
    return this.http.post<boolean>(unassignExternalAgencyUserUrl, patientExternalAgencyUser).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  unassignICTMember(ictMember: ICTMember): Observable<boolean> {
    return this.http.post<boolean>(unassignICTMemberUrl, ictMember).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  unassignCareSpecialist(patientCareSpecialist: PatientCareSpecialist): Observable<boolean> {
    return this.http.post<boolean>(unassignCareSpecialistUrl, patientCareSpecialist).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  updateICTMember(ictMember: ICTMember): Observable<boolean> {
    return this.http.post<boolean>(updateICTMemberUrl, ictMember).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  updateCareSpecialist(patientCareSpecialist: PatientCareSpecialist): Observable<boolean> {
    return this.http.post<boolean>(updateCareSpecialistUrl, patientCareSpecialist).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  careTeamMemberRoleExists(role: string, memberId: string): Observable<boolean> {
    return this.http.get<boolean>(careTeamMemberRoleExistsUrl, {
      params: {
        role: role,
        memberid: memberId
      }
    });
  }

  careTeamExternalAgencyMemberRoleExists(role: string, memberId: string): Observable<boolean> {
    return this.http.get<boolean>(careTeamExternalAgencyMemberRoleExistsUrl, {
      params: {
        role: role,
        memberid: memberId
      }
    });
  }

  revokePatientCareGiver(memberId: string, careGiverId: string, carePlanId?: number): Observable<boolean> {
    return this.http
      .post<boolean>(revokePatientCareGiverUrl, '', {
        params: {
          memberid: memberId,
          caregiverid: careGiverId,
          careplanid: carePlanId || 0
        }
      })
      .pipe(
        tap((success) => {
          this._careTeamUpdated.next(success);
        })
      );
  }

  reactivatePatientCareGiver(memberId: string, careGiverId: string, carePlanId?: number): Observable<boolean> {
    return this.http
      .post<boolean>(reactivatePatientCareGiverUrl, '', {
        params: {
          memberid: memberId,
          caregiverid: careGiverId,
          careplanid: carePlanId || 0
        }
      })
      .pipe(
        tap((success) => {
          this._careTeamUpdated.next(success);
        })
      );
  }

  getCareTeam(memberId: string, ignoreNonUsers = false, permissionedUsersOnly = false): Observable<CareTeamMember[]> {
    return this.http
      .get<CareTeamMember[]>(getCareTeamUrl, {
        params: {
          memberid: memberId,
          ignoreNonUsers: ignoreNonUsers,
          permissionedUsersOnly: permissionedUsersOnly
        }
      })
      .pipe(tap(this.populateAdditionalFields));
  }

  getPatientAssignCareTeam(
    memberId: string,
    ignoreNonUsers = false,
    permissionedUsersOnly = false
  ): Observable<CareTeamMember[]> {
    return this.http
      .get<CareTeamMember[]>(getPatientAssignCareTeamUrl, {
        params: {
          memberid: memberId,
          ignoreNonUsers: ignoreNonUsers,
          permissionedUsersOnly: permissionedUsersOnly
        }
      })
      .pipe(tap(this.populateAdditionalFields));
  }

  getAssignCareTeamTriggers(): Observable<PatientCareTeamTrigger[]> {
    return this.http.get<PatientCareTeamTrigger[]>(getCareTeamTriggerUrl);
  }

  getCareTeamRoles(memberType: string): Observable<string[]> {
    return this.http.get<string[]>(getCareTeamRolesUrl, {
      params: {
        memberType: memberType
      }
    });
  }

  getCaseLoad(careManagerId: string): Observable<CaseLoad[]> {
    return this.http
      .get<JSONResponse<CaseLoad[]>>(getCaseloadUrl, {
        params: {
          caremanagerid: careManagerId
        }
      })
      .pipe(
        map((response) => {
          return (response.status === JSONResponseStatus.SUCCESS && response.result) || [];
        })
      );
  }

  getRiskBaseCaseLoad(careManagerId: string): Observable<CaseLoad[]> {
    return this.http
      .get<JSONResponse<CaseLoad[]>>(getRiskBaseCaseLoadUrl, {
        params: {
          caremanagerid: careManagerId
        }
      })
      .pipe(
        map((response) => {
          return (response.status === JSONResponseStatus.SUCCESS && response.result) || [];
        })
      );
  }

  getPatientAssignCareTeamHistory(memberId: string): Observable<CareTeamMember[]> {
    return this.http
      .get<CareTeamMember[]>(getPatientAssignCareTeamHistoryUrl, {
        params: {
          memberid: memberId
        }
      })
      .pipe(tap(this.populateAdditionalFields));
  }

  assignCareTeamProvider(careTeamProvider: CareTeamProvider): Observable<boolean> {
    return this.http.post<boolean>(assignCareTeamProviderUrl, careTeamProvider).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  unassignCareTeamProvider(careTeamProvider: CareTeamProvider): Observable<boolean> {
    return this.http.post<boolean>(unassignCareTeamProviderUrl, careTeamProvider).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  updateCareTeamProvider(careTeamProvider: CareTeamProvider): Observable<boolean> {
    return this.http.post<boolean>(updateCareTeamProviderUrl, careTeamProvider).pipe(
      tap((success) => {
        this._careTeamUpdated.next(success);
      })
    );
  }

  private populateAdditionalFields(careTeamMembers: CareTeamMember[]) {
    careTeamMembers?.forEach((careTeamMember) => {
      const isSpecialist = careTeamMember.entitytype === 'Specialist';

      if (isSpecialist && careTeamMember.role === 'Other') {
        careTeamMember.displayRole = `${careTeamMember.role} - ${careTeamMember.otherSpecialistType}`;
      } else {
        careTeamMember.displayRole = careTeamMember.role;
      }

      if (isSpecialist || careTeamMember.entitytype === 'Other' || careTeamMember.entitytype === 'CareTeamProvider') {
        careTeamMember.displayPhone = careTeamMember.landphone;
      } else {
        careTeamMember.displayPhone = careTeamMember.mobilephone;
      }
    });
  }
  notifyUpdate(success: boolean): void {
    this._careTeamUpdated.next(success);
  }
}
